// React Components
import React, { Fragment, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

// Ant Design Components
import { Table } from 'antd';

// Utilities & Helpers
import { QUANTITY } from '@Apis/constants';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import { v4 as uuidv4 } from 'uuid';

// UI Components
import H3Text from '@Uilib/h3Text';
import PRZModal from '../../../../Common/UI/PRZModal';

// Common Components
import BatchesTable from '@Components/Common/BatchesTable';
import ProductCategoryLabel from '../../../../Common/ProductCategoryLabel';

// Styles
import './style.scss';

const StockTransferLines = (props) => {
  const { visibleColumns, cfStockTransferLine, selectedSt } = props;
  const [showPRZModal, setShowPRZModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const getPRColumns = useCallback(() => {
    const stLinesColumns = [
      {
        title: '',
        render: (record) => (
          <div className="mobile-list__item">
            <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`}>
              <H3Text
                text={`#${record?.product_sku_info?.internal_sku_code}`}
                className="mobile-list__item-number"
              />
            </Link>
            <H3Text
              text={record?.product_sku_info?.product_sku_name}
              className="mobile-list__item-info"
            />
            <H3Text
              text={`Transfer Quantity - ${QUANTITY(record.quantity, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ""}`}
              className="mobile-list__item-info"
            />
          </div>
        ),
        responsive: ["xs"],
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: "Product",
        render: (record) => (
          <Fragment>
            <div onClick={() => {
              setShowPRZModal(true);
              setSelectedDocumentId(record?.product_sku_info?.product_sku_id);
            }}>
              <a>{`#${record?.product_sku_info?.internal_sku_code}`}</a>
            </div>

            <div>
              {`${record?.product_sku_info?.ref_product_code
                ? `${record?.product_sku_info?.ref_product_code} - `
                : ""
                }${record?.product_sku_info?.product_sku_name}`?.trim()}
            </div>
            <H3Text text={record.remarks} className="table-subscript" />

            {record?.product_sku_info?.product_category_info?.category_path?.length > 0 && (
              <ProductCategoryLabel
                categoryPath={record?.product_sku_info?.product_category_info?.category_path}
                categoryName={record?.product_sku_info?.product_category_info?.category_path?.at(-1)}
                containerStyle={{ width: "fit-content" }}
              />
            )}
          </Fragment>
        ),
        responsive: ["sm", "md", "lg", "xxl"],
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'HSN/SAC Code',
        render: (item) => (
          <Fragment>
            <div>{item.product_sku_info?.hsn_code || "-"}</div>
          </Fragment>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        visible: visibleColumns?.HSN?.visible,
      },
      {
        title: 'Transfer Qty',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (
          <div>
            <div>
              {`${QUANTITY(
                selectedSt?.is_short_closed ? item?.prev_quantity : item?.quantity,
                item?.uom_info?.precision,
              )} ${item?.uom_info?.uqc?.toProperCase() || '-'}`}
            </div>
            {Number(item?.secondary_uom_qty) > 0 && (
              <div>
                {`${QUANTITY(
                  item?.secondary_uom_qty,
                  item?.product_sku_info?.secondary_uom_info?.precision,
                )} ${item?.product_sku_info?.secondary_uom_info?.uqc?.toProperCase() || '-'} (Secondary)`}
              </div>
            )}
          </div>
        ),
        visible: visibleColumns?.TRANSFER_QTY?.visible,
      },
      {
        title: 'Received Qty',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (
          <div>
            <div>
              {`${QUANTITY(item?.consumed_qty || '0', item?.uom_info?.precision)} ${item?.uom_info?.uqc?.toProperCase() || '-'}`}
            </div>
            {Number(item?.secondary_consumed_qty) > 0 && (
              <div>
                {`${QUANTITY(
                  item?.secondary_consumed_qty,
                  item?.product_sku_info?.secondary_uom_info?.precision,
                )} ${item?.product_sku_info?.secondary_uom_info?.uqc?.toProperCase() || '-'} (Secondary)`}
              </div>
            )}
          </div>
        ),
        visible: visibleColumns?.RECEIVED_QTY?.visible,
      },
      {
        title: "Short Close Qty",
        responsive: ["sm", "md", "lg", "xxl"],
        render: (item) =>
          `${QUANTITY(
            Number(item?.prev_quantity - item?.consumed_qty) || "0",
            item?.uom_info?.precision
          )} ${item?.uom_info?.uqc?.toProperCase() || "-"}`,
        visible:
          visibleColumns?.SHORT_CLOSE_QTY?.visible && selectedSt?.is_short_closed,
      },
      {
        title: "Location",
        responsive: ["sm", "md", "lg", "xxl"],
        render: (item) =>
          `${item?.source_product_batches?.[0]?.destination_inventory_location_path || "-"}`,
        visible: visibleColumns?.LOCATION?.visible,
      },
    ];

    if (cfStockTransferLine?.length) {
      stLinesColumns.splice(
        6,
        0,
        ...CustomFieldHelpers.renderCustomLineColumns(false, cfStockTransferLine, visibleColumns, null)
      );
    }

    return stLinesColumns?.filter((item) => item.visible);
  }, [cfStockTransferLine, selectedSt, visibleColumns]);

  return (
    <Fragment>
      <div className="view-st-table-wrapper">
        <Table
          bordered
          showHeader
          title={() => `Stock Transfer Items (${selectedSt?.stock_transfer_lines?.length})`}
          size="small"
          columns={
            selectedSt?.source_department_info?.tenant_id === selectedSt?.destination_department_info?.tenant_id
              ? getPRColumns()?.filter((item) => item.title !== 'RECEIVED QTY')
              : getPRColumns()
          }
          dataSource={selectedSt?.stock_transfer_lines?.map((item) => ({
            ...item,
            key: uuidv4(),
            lineCustomFields: item?.custom_fields
          }))}
          pagination={false}
          scroll={{ x: 'max-content' }}
          expandable={{
            rowExpandable: (record) => record?.source_product_batches?.length,
            expandedRowRender: (record) => (
              <BatchesTable
                title="Transferred Batches"
                batches={record?.source_product_batches}
                uomInfo={record?.uom_info}
                productInfo={record?.product_sku_info}
                expiryDays={record?.product_sku_info?.expiry_days}
                showCancel
              />
            )
          }}
        />
      </div>
      <PRZModal
        isOpen={showPRZModal}
        onClose={() => {
          setShowPRZModal(false);
          setSelectedDocumentId(null);
        }}
        entityType="product"
        documentId={selectedDocumentId}
      />
    </Fragment>
  );
};

export default StockTransferLines;
